import { addCalendarDays, addCalendarMonths } from '@fieldops/shared'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { cn } from '../../lib/cn.js'
import { addressLine } from '../../lib/format.js'
import { useJobHref } from '../../lib/job-overlay.js'
import { errorMessage, useTRPC } from '../../lib/trpc.js'
import { Icon, type IconName } from '../Icon.js'
import { useDismiss } from '../overlay.js'
import { andList, dayLabel } from '../repeat/rule.js'
import { Button, ErrorState } from '../ui.js'
import type { AgreementPage } from './AgreementGrid.js'

type Row = AgreementPage['schedules'][number]
type Slot = Row['slots'][number]
type Booking = AgreementPage['bookings'][number]
type Ref = { scheduleId: string; sequence: number }
type Move = { kind: 'date'; dueOn: string } | { kind: 'shift'; count: number; unit: 'WEEK' | 'MONTH' } | { kind: 'schedule' }

/** What was clicked: a booking on a site's Bookings row, or one visit on a service's row. */
export type VisitMenuTarget = { kind: 'booking'; booking: Booking } | { kind: 'visit'; row: Row; slot: Slot }

type View = 'main' | 'move' | 'note' | 'skip' | 'split' | 'jobNote' | 'made'

const MENU_WIDTH = 320

const ref = (visit: { scheduleId: string; sequence: number }): Ref => ({
  scheduleId: visit.scheduleId,
  sequence: visit.sequence,
})
const same = (a: Ref, b: Ref) => a.scheduleId === b.scheduleId && a.sequence === b.sequence

/**
 * Everything that can be done to a booking or a visit of an agreement, from
 * where it sits on the agreement page. Decision 96.
 *
 * A right-click opens it at the pointer; a left-click on a mark with no job
 * opens it at the mark. A booking still to make can be made into its job —
 * and opened over the page, which stays underneath — given a note, moved,
 * put back on its schedule date, skipped, or split. Each of those changes
 * only the visits on it: the rest of the agreement keeps its schedule. One
 * already made opens its job or takes a note on it.
 *
 * Every write is `agreements.*`, checked against the agreement. With
 * `canEdit` false the menu only says what the visit is and opens its job.
 */
export function VisitMenu({
  page,
  target,
  position,
  canEdit,
  onClose,
  onEditService,
  onEditSite,
}: {
  page: AgreementPage
  target: VisitMenuTarget
  position: { x: number; y: number }
  canEdit: boolean
  onClose: () => void
  onEditService?: (row: Row) => void
  onEditSite?: (siteId: string) => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const jobHref = useJobHref()
  const menu = useRef<HTMLDivElement>(null)
  const [view, setView] = useState<View>('main')
  const [madeJobId, setMadeJobId] = useState<string | null>(null)
  const [box, setBox] = useState(() => guessBox(position))

  // Placed again whenever it changes height: another view, or an error under it.
  useLayoutEffect(() => {
    const node = menu.current
    if (!node) return
    const place = () => setBox(placeBox(position, node.offsetWidth, node.offsetHeight))
    place()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(place)
    observer.observe(node)
    return () => observer.disconnect()
  }, [position])

  // The press that opened the menu is still on its way up when it mounts.
  useDismiss(menu, true, () => onClose(), { closeOnScroll: true, fromNextTick: true })

  useEffect(() => {
    menu.current?.querySelector<HTMLElement>('textarea, input, [role="menuitem"]:not([disabled])')?.focus()
  }, [view])

  const refresh = (made = false) => {
    void queryClient.invalidateQueries(trpc.agreements.pathFilter())
    void queryClient.invalidateQueries(trpc.repeat.pathFilter())
    if (made) {
      void queryClient.invalidateQueries(trpc.jobs.pathFilter())
      void queryClient.invalidateQueries(trpc.schedule.pathFilter())
    }
  }
  const done = () => {
    refresh()
    onClose()
  }
  const create = useMutation(trpc.agreements.createBookingJob.mutationOptions({ onSuccess: () => refresh(true) }))
  const move = useMutation(trpc.agreements.moveVisits.mutationOptions({ onSuccess: done }))
  const skip = useMutation(trpc.agreements.setVisitsSkipped.mutationOptions({ onSuccess: done }))
  const split = useMutation(trpc.agreements.setVisitsSplit.mutationOptions({ onSuccess: done }))
  const note = useMutation(trpc.agreements.setVisitNote.mutationOptions({ onSuccess: done }))
  const jobNote = useMutation(
    trpc.jobs.addNote.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.jobs.pathFilter())
        onClose()
      },
    }),
  )
  const failure = create.error ?? move.error ?? skip.error ?? split.error ?? note.error ?? jobNote.error

  const rows = new Map(page.schedules.map((row) => [row.id, row]))
  const booking =
    target.kind === 'booking'
      ? target.booking
      : (page.bookings.find((candidate) =>
          candidate.visits.some((visit) => same(visit, { scheduleId: target.row.id, sequence: target.slot.sequence })),
        ) ?? null)
  const visits =
    target.kind === 'booking'
      ? target.booking.visits.flatMap((visit) => {
          const row = rows.get(visit.scheduleId)
          const slot = row?.slots.find((candidate) => candidate.sequence === visit.sequence)
          return row && slot ? [{ row, slot }] : []
        })
      : [{ row: target.row, slot: target.slot }]
  const refs = visits.map(({ row, slot }) => ({ scheduleId: row.id, sequence: slot.sequence }))
  const lead = visits[0]
  if (!lead) return null

  const jobId = madeJobId ?? (target.kind === 'booking' ? target.booking.jobId : (target.slot.job?.id ?? null))
  const skipped = visits.every(({ slot }) => slot.state === 'SKIPPED')
  const many = visits.length > 1
  const noun = target.kind === 'booking' ? 'booking' : 'visit'
  const names = (list: ReadonlyArray<{ scheduleId: string }>) =>
    andList(list.map((visit) => serviceName(rows.get(visit.scheduleId))))
  const site = lead.row.site?.name ?? addressLine(lead.row.site?.address)
  const dueOn = target.kind === 'booking' ? target.booking.dueOn : target.slot.dueOn
  const ended = page.status === 'ENDED'
  const editable = canEdit && !ended
  const pausedRow = visits.find(({ row }) => row.stoppedAt !== null)
  const cannotMake =
    page.status === 'PAUSED'
      ? 'The agreement is paused, so no job is made.'
      : pausedRow
        ? `${serviceName(pausedRow.row)} is paused here, so no job is made.`
        : null
  // The visit's own booking, when it shares one with others. Create job makes all of it.
  const sharing = target.kind === 'visit' && booking && booking.visits.length > 1 ? booking : null
  const toMake = sharing ? sharing.visits.map(ref) : refs

  const make = (visitsToMake: Ref[], open: boolean) =>
    create.mutate(
      { agreementId: page.id, visits: visitsToMake },
      {
        onSuccess: (made) => {
          if (open) {
            onClose()
            void navigate(jobHref(made.jobId))
            return
          }
          setMadeJobId(made.jobId)
          setView('made')
        },
      },
    )
  const openJob = (id: string) => {
    onClose()
    void navigate(jobHref(id))
  }

  /** Arrow keys walk the items, as in any menu. */
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
    const items = [...(menu.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([disabled])') ?? [])]
    if (items.length === 0) return
    event.preventDefault()
    const at = items.indexOf(document.activeElement as HTMLElement)
    const next = event.key === 'ArrowDown' ? (at + 1) % items.length : (at - 1 + items.length) % items.length
    items[next]?.focus()
  }

  const title = names(refs)
  const status = jobId
    ? lead.slot.state === 'DONE'
      ? 'Done'
      : 'Job made'
    : skipped
      ? 'Skipped'
      : page.status === 'PAUSED' || pausedRow
        ? 'Paused'
        : visits.some(({ slot }) => slot.state === 'OVERDUE')
          ? 'Overdue'
          : 'Planned'
  const flags = [
    many ? `${visits.length} visits` : null,
    !jobId && visits.some(({ slot }) => slot.moved) ? 'moved' : null,
    !jobId && booking?.split ? 'split off' : null,
  ].filter(Boolean)

  return createPortal(
    <div
      ref={menu}
      role="menu"
      aria-label={`${noun === 'booking' ? 'Booking' : 'Visit'}: ${title}, ${dayLabel(dueOn)}`}
      onPointerDown={(event) => event.stopPropagation()}
      onContextMenu={(event) => event.preventDefault()}
      onKeyDown={onKeyDown}
      style={{ left: box.left, top: box.top, width: MENU_WIDTH }}
      className="fixed z-50 overflow-hidden rounded-lg bg-white shadow-[0_16px_40px_rgb(15_23_42_/_0.22),0_4px_12px_rgb(15_23_42_/_0.1)] ring-1 ring-ink-200"
    >
      <div className="border-b border-ink-100 px-3.5 pt-3 pb-2.5">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <p className="truncate text-[10.5px] font-bold tracking-[0.08em] text-ink-500 uppercase">
              {[status, ...flags].join(' · ')}
            </p>
            <p className="mt-0.5 line-clamp-2 font-display text-[14.5px] leading-snug font-extrabold text-ink-900">{title}</p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="shrink-0 rounded-md p-0.5 text-ink-400 transition-colors hover:bg-ink-100 hover:text-ink-900"
          >
            <Icon name="close" className="size-4" />
          </button>
        </div>
        <p className="mt-0.5 text-[12px] text-ink-500 tabular-nums">
          {`${lead.slot.tentative && !jobId ? 'About ' : 'Due '}${dayLabel(dueOn)}`}
        </p>
        <p title={site} className="truncate text-[12px] text-ink-500">
          {site}
        </p>
        {skipped && lead.slot.skipReason ? (
          <p className="mt-1 text-[12px] text-ink-700">{`Skipped: ${lead.slot.skipReason}`}</p>
        ) : null}
      </div>

      {view === 'made' && jobId ? (
        <>
          <div role="status" className="flex items-center gap-2 bg-brand-50 px-3.5 py-2 text-[12.5px] font-medium text-brand-800">
            <Icon name="check" className="size-3.5 shrink-0 text-brand-700" strokeWidth={2.5} />
            {create.data && create.data.scheduleIds.length > 1 ? `Job made for all ${create.data.scheduleIds.length} visits.` : 'Job made.'}
          </div>
          <Items>
            <Item icon="jobs" strong onClick={() => openJob(jobId)} trailing={<Icon name="forward" className="size-3.5" />}>
              Open job
            </Item>
            <Item icon="note" onClick={() => setView('jobNote')}>
              Add a note to the job…
            </Item>
          </Items>
        </>
      ) : view === 'move' ? (
        <MoveView
          dueOn={dueOn}
          many={many}
          noun={noun}
          followsCompletion={visits.some(({ row }) => row.anchor === 'COMPLETED_DATE')}
          pending={move.isPending}
          onBack={() => setView('main')}
          onMove={(to) => move.mutate({ agreementId: page.id, visits: refs, to })}
        />
      ) : view === 'note' ? (
        <TextView
          label={many ? 'Note for this booking’s job' : 'Note for this visit’s job'}
          hint="Written into the job’s brief when it’s made."
          initial={lead.slot.note ?? ''}
          others={visits.slice(1).flatMap(({ row, slot }) => (slot.note ? [`${serviceName(row)}: ${slot.note}`] : []))}
          action="Save note"
          allowEmpty
          pending={note.isPending}
          onBack={() => setView('main')}
          onSave={(text) =>
            note.mutate({ agreementId: page.id, scheduleId: lead.row.id, sequence: lead.slot.sequence, note: text || null })
          }
        />
      ) : view === 'jobNote' && jobId ? (
        <TextView
          label="Note on the job"
          hint="Added to the job’s notes."
          initial=""
          action="Add note"
          pending={jobNote.isPending}
          onBack={() => setView(madeJobId ? 'made' : 'main')}
          onSave={(text) => jobNote.mutate({ jobId, body: text, isPinned: false })}
        />
      ) : view === 'skip' ? (
        <TextView
          label="Why it’s skipped (optional)"
          hint={`Nothing is made for ${many ? 'these visits' : 'it'}. The visits after ${many ? 'them' : 'it'} stay on schedule.`}
          initial=""
          single
          allowEmpty
          action={`Skip this ${noun}`}
          danger
          pending={skip.isPending}
          onBack={() => setView('main')}
          onSave={(reason) => skip.mutate({ agreementId: page.id, visits: refs, skipped: true, reason: reason || null })}
        />
      ) : view === 'split' && booking ? (
        <SplitView
          booking={booking}
          names={(list) => names(list)}
          combineDays={page.combineDays}
          pending={split.isPending}
          onBack={() => setView('main')}
          onSplit={(list, value) => split.mutate({ agreementId: page.id, visits: list.map(ref), split: value })}
        />
      ) : (
        <>
          {jobId ? (
            <Items>
              <Item icon="jobs" strong onClick={() => openJob(jobId)} trailing={<Icon name="forward" className="size-3.5" />}>
                Open job
              </Item>
              {editable ? (
                <Item icon="note" onClick={() => setView('jobNote')}>
                  Add a note to the job…
                </Item>
              ) : null}
            </Items>
          ) : skipped ? (
            editable ? (
              <Items>
                <Item
                  icon="undo"
                  disabled={skip.isPending}
                  onClick={() => skip.mutate({ agreementId: page.id, visits: refs, skipped: false })}
                >
                  Don’t skip it
                </Item>
              </Items>
            ) : null
          ) : editable ? (
            <>
              <Items>
                <Item
                  icon="plus"
                  strong
                  disabled={cannotMake !== null || create.isPending}
                  hint={cannotMake}
                  onClick={() => make(toMake, false)}
                  trailing={
                    toMake.length > 1 ? (
                      <span className="text-[11.5px] font-normal text-ink-400">{`${toMake.length} visits`}</span>
                    ) : null
                  }
                >
                  Create job
                </Item>
                <Item
                  icon="jobs"
                  disabled={cannotMake !== null || create.isPending}
                  onClick={() => make(toMake, true)}
                  trailing={<Icon name="forward" className="size-3.5" />}
                >
                  Create and open job
                </Item>
                {sharing ? (
                  <Item
                    icon="plus"
                    disabled={cannotMake !== null || create.isPending}
                    hint={`${names(sharing.visits.filter((visit) => !same(visit, refs[0]!)))} stay for later.`}
                    onClick={() => make(refs, false)}
                  >
                    Create job for this visit alone
                  </Item>
                ) : null}
              </Items>
              <Items>
                <Item
                  icon="note"
                  onClick={() => setView('note')}
                  hint={lead.slot.note ? <span className="line-clamp-2">{lead.slot.note}</span> : null}
                >
                  {lead.slot.note ? 'Edit the note…' : 'Add a note…'}
                </Item>
                <Item icon="schedule" onClick={() => setView('move')}>
                  {many ? 'Move the booking…' : `Move the ${noun}…`}
                </Item>
                {visits.some(({ slot }) => slot.moved) ? (
                  <Item
                    icon="undo"
                    disabled={move.isPending}
                    onClick={() => move.mutate({ agreementId: page.id, visits: refs, to: { kind: 'schedule' } })}
                  >
                    Back on its schedule date
                  </Item>
                ) : null}
                {booking && booking.visits.length > 1 && target.kind === 'booking' ? (
                  <Item icon="snip" onClick={() => setView('split')}>
                    Split it…
                  </Item>
                ) : sharing ? (
                  <Item
                    icon="snip"
                    disabled={split.isPending}
                    hint={`Made as a job of its own, not with ${names(sharing.visits.filter((visit) => !same(visit, refs[0]!)))}.`}
                    onClick={() => split.mutate({ agreementId: page.id, visits: refs, split: true })}
                  >
                    Split it off
                  </Item>
                ) : booking?.split && booking.putBack.length > 0 ? (
                  <Item
                    icon="merge"
                    disabled={split.isPending || booking.rejoin.length === 0}
                    hint={booking.rejoin.length === 0 ? `Nothing else here is due within ${page.combineDays} days now.` : null}
                    onClick={() => split.mutate({ agreementId: page.id, visits: booking.putBack.map(ref), split: false })}
                  >
                    {booking.rejoin.length > 0 ? `Put back with ${names(booking.rejoin)}` : 'Put back'}
                  </Item>
                ) : null}
                <Item icon="stop" danger onClick={() => setView('skip')}>
                  {`Skip this ${noun}…`}
                </Item>
              </Items>
            </>
          ) : null}

          {editable && (onEditService || onEditSite) ? (
            <Items>
              {onEditService
                ? [...new Map(visits.map(({ row }) => [row.id, row])).values()].map((row) => (
                    <Item
                      key={row.id}
                      icon="pencil"
                      quiet
                      onClick={() => {
                        onClose()
                        onEditService(row)
                      }}
                    >
                      {`Edit ${serviceName(row)}…`}
                    </Item>
                  ))
                : null}
              {onEditSite ? (
                <Item
                  icon="pencil"
                  quiet
                  onClick={() => {
                    onClose()
                    onEditSite(lead.row.siteId)
                  }}
                >
                  Edit site…
                </Item>
              ) : null}
            </Items>
          ) : null}
        </>
      )}

      {failure ? (
        <div className="border-t border-ink-100 px-3 py-2">
          <ErrorState message={errorMessage(failure)} />
        </div>
      ) : null}
    </div>,
    document.body,
  )
}

export function serviceName(row: Pick<Row, 'services'> | undefined): string {
  return row?.services.map((service) => service.name).join(', ') || 'Service'
}

function Items({ children }: { children: ReactNode }) {
  return <div className="border-b border-ink-100 py-1 last:border-b-0">{children}</div>
}

function Item({
  icon,
  children,
  onClick,
  disabled = false,
  strong = false,
  quiet = false,
  danger = false,
  hint = null,
  trailing = null,
}: {
  icon: IconName
  children: ReactNode
  onClick: () => void
  disabled?: boolean
  strong?: boolean
  quiet?: boolean
  danger?: boolean
  hint?: ReactNode
  trailing?: ReactNode
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex w-full items-start gap-2.5 px-3 py-[7px] text-left text-[13px] transition-colors outline-none',
        'focus-visible:bg-ink-50 disabled:cursor-default disabled:opacity-55',
        danger ? 'text-red-700 hover:bg-red-50 focus-visible:bg-red-50' : 'text-ink-800 hover:bg-ink-50',
        strong ? 'font-semibold text-ink-900' : quiet ? 'font-normal text-ink-600' : 'font-medium',
      )}
    >
      <Icon name={icon} className={cn('mt-0.5 size-3.5 shrink-0', danger ? 'text-red-600' : 'text-ink-400')} />
      <span className="flex min-w-0 flex-1 flex-col">
        <span>{children}</span>
        {hint ? <span className="text-[11.5px] leading-snug font-normal text-ink-500">{hint}</span> : null}
      </span>
      {trailing ? <span className="mt-0.5 shrink-0 text-ink-400">{trailing}</span> : null}
    </button>
  )
}

function BackHeading({ children, onBack }: { children: ReactNode; onBack: () => void }) {
  return (
    <div className="flex items-center gap-1.5 px-2 pt-2 pb-1">
      <button
        type="button"
        aria-label="Back"
        onClick={onBack}
        className="rounded-md p-1 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
      >
        <Icon name="back" className="size-3.5" />
      </button>
      <span className="text-[12px] font-semibold text-ink-800">{children}</span>
    </div>
  )
}

/**
 * Move by a week or a month either way, or to a day. Only the visits on this
 * booking move; the server refuses one that would pass a neighbour.
 */
function MoveView({
  dueOn,
  many,
  noun,
  followsCompletion,
  pending,
  onBack,
  onMove,
}: {
  dueOn: string
  many: boolean
  noun: string
  followsCompletion: boolean
  pending: boolean
  onBack: () => void
  onMove: (to: Move) => void
}) {
  const [day, setDay] = useState(dueOn)
  const shifts: ReadonlyArray<{ label: string; count: number; unit: 'WEEK' | 'MONTH' }> = [
    { label: 'A week earlier', count: -1, unit: 'WEEK' },
    { label: 'A week later', count: 1, unit: 'WEEK' },
    { label: 'Two weeks later', count: 2, unit: 'WEEK' },
    { label: 'A month later', count: 1, unit: 'MONTH' },
  ]

  return (
    <div className="pb-2">
      <BackHeading onBack={onBack}>{`Move the ${noun} · due ${dayLabel(dueOn)}`}</BackHeading>
      <div className="py-0.5">
        {shifts.map((shift) => (
          <Item
            key={shift.label}
            icon={shift.count < 0 ? 'back' : 'forward'}
            disabled={pending}
            onClick={() => onMove({ kind: 'shift', count: shift.count, unit: shift.unit })}
            trailing={
              <span className="text-[11.5px] text-ink-400 tabular-nums">
                {dayLabel(
                  shift.unit === 'WEEK' ? addCalendarDays(dueOn, shift.count * 7) : addCalendarMonths(dueOn, shift.count),
                  { year: false },
                )}
              </span>
            }
          >
            {shift.label}
          </Item>
        ))}
      </div>
      <form
        className="flex items-center gap-2 px-3 pt-1.5"
        onSubmit={(event) => {
          event.preventDefault()
          if (day && day !== dueOn) onMove({ kind: 'date', dueOn: day })
        }}
      >
        <input
          type="date"
          aria-label="Move to"
          value={day}
          onChange={(event) => setDay(event.target.value)}
          className="min-h-[var(--control-sm)] min-w-0 flex-1 rounded-md bg-white px-2 text-[13px] text-ink-900 ring-1 ring-ink-300 ring-inset focus:ring-2 focus:ring-brand-600 focus:outline-none"
        />
        <Button size="sm" type="submit" variant="primary" loading={pending} disabled={!day || day === dueOn}>
          Move
        </Button>
      </form>
      <p className="px-3 pt-2 text-[11.5px] leading-snug text-ink-500">
        {followsCompletion
          ? `Only ${many ? 'these visits move' : 'this one moves'}. The next one is counted from when it’s done.`
          : `Only ${many ? 'the visits on this booking move' : 'this one moves'}. The visits after ${many ? 'them' : 'it'} stay on schedule.`}
      </p>
    </div>
  )
}

/** A note, a reason or a job note: one box and one button. */
function TextView({
  label,
  hint,
  initial,
  others = [],
  action,
  single = false,
  allowEmpty = false,
  danger = false,
  pending,
  onBack,
  onSave,
}: {
  label: string
  hint: string
  initial: string
  others?: readonly string[]
  action: string
  single?: boolean
  allowEmpty?: boolean
  danger?: boolean
  pending: boolean
  onBack: () => void
  onSave: (text: string) => void
}) {
  const [text, setText] = useState(initial)
  const control =
    'w-full rounded-md bg-white px-2 py-1.5 text-[13px] leading-snug text-ink-900 ring-1 ring-ink-300 ring-inset placeholder:text-ink-400 focus:ring-2 focus:ring-brand-600 focus:outline-none'
  const save = () => {
    if (!allowEmpty && !text.trim()) return
    onSave(text.trim())
  }

  return (
    <form
      className="flex flex-col gap-2 pb-3"
      onSubmit={(event) => {
        event.preventDefault()
        save()
      }}
    >
      <BackHeading onBack={onBack}>{label}</BackHeading>
      <div className="flex flex-col gap-2 px-3">
        {single ? (
          <input aria-label={label} value={text} onChange={(event) => setText(event.target.value)} className={cn(control, 'min-h-[var(--control-sm)]')} />
        ) : (
          <textarea
            aria-label={label}
            value={text}
            rows={4}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) save()
            }}
            className={cn(control, 'resize-none')}
          />
        )}
        {others.length > 0 ? (
          <ul className="flex flex-col gap-0.5 text-[11.5px] text-ink-600">
            {others.map((other) => (
              <li key={other}>{other}</li>
            ))}
          </ul>
        ) : null}
        <p className="text-[11.5px] leading-snug text-ink-500">{hint}</p>
        <div className="flex justify-end gap-2">
          <Button size="sm" type="button" onClick={onBack}>
            Cancel
          </Button>
          <Button
            size="sm"
            type="submit"
            variant={danger ? 'danger' : 'primary'}
            loading={pending}
            disabled={!allowEmpty && !text.trim()}
          >
            {action}
          </Button>
        </div>
      </div>
    </form>
  )
}

/**
 * What is on a booking still to make, and splitting it: one visit off, or
 * every one when there are more than two. Decision 96.
 */
function SplitView({
  booking,
  names,
  combineDays,
  pending,
  onBack,
  onSplit,
}: {
  booking: Booking
  names: (list: ReadonlyArray<{ scheduleId: string }>) => string
  combineDays: number
  pending: boolean
  onBack: () => void
  onSplit: (visits: ReadonlyArray<Ref>, split: boolean) => void
}) {
  return (
    <div className="pb-2">
      <BackHeading onBack={onBack}>{`One booking · ${booking.visits.length} visits`}</BackHeading>
      <ul className="flex flex-col px-3">
        {booking.visits.map((visit) => (
          <li key={`${visit.scheduleId}:${visit.sequence}`} className="flex min-h-[var(--control-sm)] items-center gap-2 text-[13px] text-ink-800">
            <span className="min-w-0 flex-1 truncate font-medium">{names([visit])}</span>
            <span className="shrink-0 text-[12px] text-ink-500 tabular-nums">{dayLabel(visit.dueOn, { year: false })}</span>
            <Button size="sm" variant="ghost" disabled={pending} aria-label={`Split ${names([visit])} off`} onClick={() => onSplit([visit], true)}>
              Split off
            </Button>
          </li>
        ))}
      </ul>
      <p className="px-3 pt-1 text-[11.5px] leading-snug text-ink-500">
        {`Due within ${combineDays} days of each other at this site, so they’re made as one job. A visit split off is made on its own.`}
      </p>
      {booking.visits.length > 2 ? (
        <div className="flex justify-end px-3 pt-2">
          <Button size="sm" loading={pending} onClick={() => onSplit(booking.visits, true)}>
            {`Split into ${booking.visits.length} bookings`}
          </Button>
        </div>
      ) : null}
    </div>
  )
}

function guessBox(position: { x: number; y: number }) {
  return {
    left: Math.min(position.x + 4, Math.max(8, window.innerWidth - MENU_WIDTH - 8)),
    top: position.y + 4,
  }
}

function placeBox(position: { x: number; y: number }, width: number, height: number) {
  const pad = 8
  const left = Math.min(Math.max(pad, position.x + 4), window.innerWidth - width - pad)
  const below = position.y + 4
  const top = below + height + pad > window.innerHeight ? Math.max(pad, position.y - height - 4) : below
  return { left, top }
}
